'use client';

import {
  Alert,
  Box,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { FormTextField } from '@/components/form/formTextField';
import { NeptuneParameterGroupSelect } from '@/components/selectors/neptuneParameterGroupSelect';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreNeptuneState } from '../restoreNeptuneWizard';

const requiredValidation = {
  validateInput: (v: string) => !!v?.trim(),
  errorMessage: () => 'Required' as const,
};

export const NeptuneConfiguration = (props: StepProps<RestoreNeptuneState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);
  const [keepOriginalSettings, setKeepOriginalSettings] = useState(true);

  const hasSourceAccount = !!props.wizardState.restoreAccount;

  const neptuneProps = props.wizardState.snapshot.neptuneSnapshotProperties;
  const awsBackupVaultInfo = useMemo(() => {
    if (!neptuneProps) return undefined;
    return {
      cloudProvider: Provider.Aws,
      region: neptuneProps.region || props.wizardState.initialRegionName,
    };
  }, [neptuneProps, props.wizardState.initialRegionName]);

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  useEffect(() => {
    if (
      !props.wizardState.restoreAccount &&
      props.wizardState.accounts?.length
    ) {
      props.setWizardState((state) => ({
        ...state,
        restoreAccount: state.accounts?.[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.accounts]);

  const canGoNext = () =>
    hasSourceAccount &&
    !!props.wizardState.dbClusterIdentifier?.trim() &&
    !isRestoring;

  const onNextClick = async () => {
    if (!props.wizardState.restoreAccount || !props.wizardState.regionName) {
      return;
    }

    setIsRestoring(true);
    setError(undefined);

    try {
      await dal.restore.neptune.restore(
        props.wizardState.resourceId,
        props.wizardState.snapshot.id,
        {
          restoreAccountId: props.wizardState.restoreAccount.id,
          destination: {
            region: props.wizardState.regionName,
            dbClusterIdentifier: props.wizardState.dbClusterIdentifier!,
            port: props.wizardState.port,
            dbClusterParameterGroup: props.wizardState.dbClusterParameterGroup,
            iamDbAuthenticationEnabled: keepOriginalSettings
              ? undefined
              : props.wizardState.iamDbAuthenticationEnabled,
            encryptionKey: getEncryptionKey(props.wizardState.encryption),
            enableCloudwatchLogs: keepOriginalSettings
              ? undefined
              : props.wizardState.enableCloudwatchLogs,
            tags: props.wizardState.tags,
          },
        }
      );
      await props.finish();
    } catch (e) {
      setError(await extractErrorMessage(e));
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      hideStepper
      navigationComponent={
        error ? (
          <Stack
            alignItems='center'
            direction='row'
            sx={{ color: 'var(--mui-palette-error-main)' }}
          >
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={[]}
      stepperIndex={0}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          backupVaultInfo={awsBackupVaultInfo}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        {!hasSourceAccount && (
          <Alert severity='info' className='mb-[20px]'>
            <Typography variant='body2'>
              {props.wizardState.emptyMessage}
            </Typography>
          </Alert>
        )}

        <Box className='mt-[20px]'>
          <BrowserGrid columns={2} gap='24px'>
            <FormTextField
              title='DB cluster identifier'
              dataTestId='dbClusterIdentifierTextField'
              validation={requiredValidation}
              placeholder='Enter cluster identifier'
              value={props.wizardState.dbClusterIdentifier || ''}
              onChange={(e) =>
                props.setWizardState((state) => ({
                  ...state,
                  dbClusterIdentifier: e.target.value,
                }))
              }
            />

            <FormControl size='small'>
              <FormLabel>Port</FormLabel>
              <TextField
                data-testid='portTextField'
                size='small'
                type='number'
                placeholder='8182'
                value={props.wizardState.port ?? ''}
                onChange={(e) =>
                  props.setWizardState((state) => ({
                    ...state,
                    port: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              />
            </FormControl>

            <NeptuneParameterGroupSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              parameterGroupName={props.wizardState.dbClusterParameterGroup}
              onChange={(dbClusterParameterGroup) =>
                props.setWizardState((state) => ({
                  ...state,
                  dbClusterParameterGroup,
                }))
              }
            />
          </BrowserGrid>
        </Box>
      </Box>

      {props.wizardState.regionName && hasSourceAccount && (
        <>
          <Divider />
          <CardContent className='px-0'>
            <EncryptionComponent
              accountId={props.wizardState.restoreAccount!.id}
              regionName={props.wizardState.regionName}
              onChange={(encryption) =>
                props.setWizardState((state) => ({ ...state, encryption }))
              }
              encryption={props.wizardState.encryption}
            />
          </CardContent>
        </>
      )}

      <Divider />
      <CardContent className='px-0'>
        <Stack className='w-full px-[40px]' gap='24px'>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='body1'>Keep original settings</Typography>
            <Switch
              size='small'
              checked={keepOriginalSettings}
              onChange={() => {
                const newValue = !keepOriginalSettings;
                setKeepOriginalSettings(newValue);
                if (newValue) {
                  props.setWizardState((state) => ({
                    ...state,
                    iamDbAuthenticationEnabled:
                      state.originalIamDbAuthenticationEnabled,
                    enableCloudwatchLogs: [...state.originalCloudwatchLogs],
                  }));
                }
              }}
            />
          </Stack>
          {keepOriginalSettings && (
            <Box>
              <Table size='small'>
                <TableBody>
                  <TableRow>
                    <TableCell className='border-0 px-0 w-[300px]'>
                      IAM DB Authentication
                    </TableCell>
                    <TableCell className='border-0 px-0'>
                      {props.wizardState.originalIamDbAuthenticationEnabled
                        ? 'Enabled'
                        : 'Disabled'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className='border-0 px-0 w-[300px]'>
                      CloudWatch Log exports
                    </TableCell>
                    <TableCell className='border-0 px-0'>
                      {props.wizardState.originalCloudwatchLogs.length > 0
                        ? props.wizardState.originalCloudwatchLogs.join(', ')
                        : 'None'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
          {!keepOriginalSettings && (
            <Box>
              <Stack gap='24px'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={props.wizardState.iamDbAuthenticationEnabled}
                      onChange={(e) =>
                        props.setWizardState((state) => ({
                          ...state,
                          iamDbAuthenticationEnabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label='Enable IAM DB authentication'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={props.wizardState.enableCloudwatchLogs.includes(
                        'audit'
                      )}
                      onChange={() =>
                        props.setWizardState((state) => {
                          const logs = state.enableCloudwatchLogs.includes(
                            'audit'
                          )
                            ? state.enableCloudwatchLogs.filter(
                                (l) => l !== 'audit'
                              )
                            : [...state.enableCloudwatchLogs, 'audit'];
                          return { ...state, enableCloudwatchLogs: logs };
                        })
                      }
                    />
                  }
                  label='Export audit logs to CloudWatch'
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>

      <Divider />

      <CardContent className='px-0'>
        <TagsSection
          tags={props.wizardState.tags}
          initialTags={props.wizardState.initialTags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          onChange={(tags: Record<string, string>) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
          setKeepOriginalTags={(keepOriginalTags: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags,
            }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
